import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col, Dropdown } from 'react-bootstrap';
import { apiService } from '../services/api';
import './UserRegistrationModal.css';

const UserRegistrationModal = ({ show, onHide, onUserAdded, currentUser }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'user',
    assetId: '',
    department: '',
    bio: '',
    photo: ''
  });
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const isSuperAdmin = currentUser?.role === 'superAdmin';
  const isCompanyAdmin = currentUser?.role === 'companyAdmin';

  // Load companies when modal opens
  useEffect(() => {
    if (show) {
      if (isSuperAdmin) {
        loadCompanies();
      } else {
        setCompanies([]);
      }
      setCompanySearch('');
      setShowCompanyDropdown(false);

      // Pre-fill for company admins
      if (isCompanyAdmin) {
        setFormData(prev => ({
          ...prev,
          role: 'user',
          assetId: currentUser?.assetId || ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          role: 'user',
          assetId: ''
        }));
      }
    }
  }, [show, isSuperAdmin, isCompanyAdmin, currentUser]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await apiService.getCompaniesForRegistration();
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Failed to load companies:', err);
      setError('Failed to load companies. Please try again.');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCompanySelect = (companyId) => {
    setFormData(prev => ({
      ...prev,
      assetId: companyId
    }));
    setShowCompanyDropdown(false);
  };

  const filteredCompanies = companies.filter(company => {
    if (!companySearch.trim()) return true;
    const searchValue = companySearch.toLowerCase();
    const companyLabel = `${company.company || ''} ${company.branch || ''} ${company.email || ''}`.toLowerCase();
    return companyLabel.includes(searchValue);
  });

  const selectedCompanyLabel = (() => {
    const selected = companies.find(c => `${c.id}` === `${formData.assetId}`);
    if (!selected) return 'Select a company (optional)';
    return `${selected.company} ${selected.branch ? `- ${selected.branch}` : ''}`.trim();
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await apiService.registerUser({
        ...formData,
        role: isCompanyAdmin ? 'user' : formData.role,
        assetId: isCompanyAdmin ? (currentUser?.assetId || null) : (formData.assetId || null) // Convert empty string to null
      });
      
      setSuccess('User registered successfully!');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: isCompanyAdmin ? 'user' : 'user',
        assetId: isCompanyAdmin ? (currentUser?.assetId || '') : '',
        department: '',
        bio: '',
        photo: ''
      });
      
      // Call the callback to refresh the users list
      if (onUserAdded) {
        onUserAdded(data.data);
      }
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'user',
      assetId: '',
      department: '',
      bio: '',
      photo: ''
    });
    setError('');
    setSuccess('');
    setShowCompanyDropdown(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered className="user-registration-modal">
      <Modal.Header closeButton>
        <Modal.Title>Add New User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" onClose={() => setSuccess('')} dismissible>
            {success}
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>First Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  minLength={2}
                  maxLength={50}
                  placeholder="Enter first name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Last Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  minLength={2}
                  maxLength={50}
                  placeholder="Enter last name"
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Form.Group className="mb-3">
            <Form.Label>Email Address *</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="Enter email address"
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Password *</Form.Label>
            <Form.Control
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              minLength={6}
              placeholder="Enter password (minimum 6 characters)"
            />
            <Form.Text className="text-muted">
              Password must be at least 6 characters long
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Role *</Form.Label>
            <Form.Select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              required
              disabled={!isSuperAdmin || isCompanyAdmin}
            >
              <option value="user">User</option>
              {!isCompanyAdmin && <option value="companyAdmin">Company Admin</option>}
              {isSuperAdmin && <option value="superAdmin">Super Admin</option>}
            </Form.Select>
            {isCompanyAdmin && (
              <Form.Text className="text-muted">
                Role is fixed to User for company admins.
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Company Assignment</Form.Label>
            {isCompanyAdmin ? (
              <Form.Control
                type="text"
                value={currentUser?.company ? `${currentUser.company.company}${currentUser.company.branch ? ` - ${currentUser.company.branch}` : ''}` : 'Assigned to your company'}
                disabled
                readOnly
              />
            ) : (
              <Dropdown
                className="w-100"
                show={showCompanyDropdown}
                onToggle={(isOpen) => setShowCompanyDropdown(isOpen)}
              >
                <Dropdown.Toggle
                  id="company-assignment-dropdown"
                  className="form-select text-start w-100"
                  variant="light"
                  style={{ backgroundColor: '#fff', borderColor: '#ced4da', color: '#212529', fontSize: '0.875rem', fontWeight: 400 }}
                  disabled={loadingCompanies}
                >
                  {selectedCompanyLabel}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100 p-0">
                  <div className="p-2 border-bottom">
                    <Form.Control
                      autoFocus
                      type="text"
                      placeholder="Search companies"
                      value={companySearch}
                      onChange={(e) => setCompanySearch(e.target.value)}
                      disabled={loadingCompanies}
                    />
                  </div>
                  <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                    <Dropdown.Item
                      key="none"
                      onClick={() => handleCompanySelect('')}
                      style={{ fontSize: '0.875rem', fontWeight: 400, color: '#212529' }}
                    >
                      Select a company (optional)
                    </Dropdown.Item>
                    {filteredCompanies.map(company => (
                      <Dropdown.Item
                        key={company.id}
                        onClick={() => handleCompanySelect(company.id)}
                        style={{ fontSize: '0.875rem', fontWeight: 400, color: '#212529' }}
                      >
                        {company.company} {company.branch ? `- ${company.branch}` : ''}
                      </Dropdown.Item>
                    ))}
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            )}
            {loadingCompanies && isSuperAdmin && (
              <Form.Text className="text-muted">
                <Spinner size="sm" className="me-1" />
                Loading companies...
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Department</Form.Label>
            <Form.Control
              type="text"
              name="department"
              value={formData.department}
              onChange={handleInputChange}
              placeholder="Enter department"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Bio</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              placeholder="Enter user biography"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Photo URL</Form.Label>
            <Form.Control
              type="url"
              name="photo"
              value={formData.photo}
              onChange={handleInputChange}
              placeholder="Enter photo URL"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={loading || !formData.firstName || !formData.lastName || !formData.email || !formData.password}
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Registering...
            </>
          ) : (
            'Register User'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserRegistrationModal;
